import React, {useRef, useState} from 'react';
import dayjs, {Dayjs} from 'dayjs';
import {Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField, Typography} from '@mui/material';
import {DateTimePicker} from '@mui/x-date-pickers';
import {TaskChanges, useUpdateTask} from '../../data/tasks';
import {useBoard} from '../../data/board';
import {BoardTask} from '../../types/task';
import {LIMITS} from '../../contract';
import {apiErrorMessage} from '../../utilities/apiError';
import {useTaskFeedback} from '../../providers/TaskFeedbackProvider';

export function EditTaskDialog({task, boardId, onClose, readOnly = false}: {task: BoardTask, boardId: string, onClose: () => void, readOnly?: boolean}) {
    const original = useRef(task).current;
    const [title, setTitle] = useState(task.title ?? '');
    const [description, setDescription] = useState(task.description ?? '');
    const [date, setDate] = useState<Dayjs | null>(task.date ? dayjs(task.date) : null);
    const [sectionId, setSectionId] = useState(task.sectionId);
    const [error, setError] = useState('');
    const {data: board} = useBoard(boardId, {fetch: false});
    const {mutateAsync: update, isPending} = useUpdateTask(boardId);
    const show = useTaskFeedback();
    const invalidDate = date !== null && !date.isValid();
    const changes: TaskChanges = {};
    if (title.trim() !== (original.title ?? '')) changes.title = title.trim();
    if (description.trim() !== (original.description ?? '')) changes.description = description.trim();
    if ((date?.valueOf() ?? null) !== (original.date ? dayjs(original.date).valueOf() : null) && !invalidDate) changes.date = date ? date.toISOString() : null;
    if (sectionId !== original.sectionId) changes.sectionId = sectionId;
    const changed = Object.keys(changes).length > 0;
    async function save(event: React.FormEvent) {
        event.preventDefault();
        if (readOnly || !changed || !title.trim() || invalidDate || isPending) return;
        setError('');
        try {
            await update({id: task.id, changes});
            show({message: 'Task saved'}); onClose();
        } catch (failure) { setError(apiErrorMessage(failure, 'Your changes could not be saved. Please try again.')); }
    }
    return <Dialog open onClose={isPending ? undefined : onClose} fullWidth maxWidth="sm" aria-labelledby="edit-task-title">
        <form onSubmit={save}>
            <DialogTitle id="edit-task-title">{readOnly ? 'Task details' : 'Edit task'}</DialogTitle>
            <DialogContent>{readOnly ? <Stack spacing={2} sx={{pt: 1}}>
                <Typography variant="h5">{task.title || 'Untitled'}</Typography>
                <Typography color="text.secondary">{task.date ? dayjs(task.date).format('dddd, MMMM D · h:mm A') : 'Anytime · no date set'}</Typography>
                {task.description && <Typography sx={{whiteSpace: 'pre-wrap', overflowWrap: 'anywhere'}}>{task.description}</Typography>}
                <Typography variant="body2" color="text.secondary">Section: {board?.sectionsById[task.sectionId]?.title || 'Untitled section'}</Typography>
            </Stack> : <Stack spacing={2.5} sx={{pt: 1}}>
                {error && <Alert severity="error">{error}</Alert>}
                <TextField autoFocus label="Task name" value={title} onChange={(e) => setTitle(e.target.value)} required fullWidth disabled={isPending || readOnly} slotProps={{htmlInput: {maxLength: LIMITS.title}}}/>
                <DateTimePicker label="Date and time" value={date} onChange={setDate} disabled={isPending || readOnly} slotProps={{textField: {fullWidth: true, helperText: invalidDate ? 'Choose a valid date and time' : 'Leave blank for an undated task'}, actionBar: {actions: ['clear', 'cancel', 'accept']}}}/>
                <TextField label="Description" value={description} onChange={(e) => setDescription(e.target.value)} multiline minRows={3} fullWidth disabled={isPending || readOnly} slotProps={{htmlInput: {maxLength: LIMITS.description}}}/>
                {!!board?.sectionOrder.length && <TextField select label="Section" value={sectionId} onChange={(e) => setSectionId(e.target.value)} disabled={isPending || readOnly}>
                    {board.sectionOrder.map((id) => <MenuItem key={id} value={id}>{board.sectionsById[id]?.title || 'Untitled section'}</MenuItem>)}
                </TextField>}
            </Stack>}</DialogContent>
            <DialogActions sx={{px: 3, pb: 3, pt: 1}}>
                <Button onClick={onClose} disabled={isPending}>{readOnly ? 'Close' : 'Cancel'}</Button>
                {!readOnly && <Button type="submit" variant="contained" disabled={isPending || !changed || !title.trim() || invalidDate} startIcon={isPending ? <CircularProgress size={16} color="inherit"/> : undefined}>{isPending ? 'Saving…' : 'Save changes'}</Button>}
            </DialogActions>
        </form>
    </Dialog>;
}
